/****************************************************
 * Module Name:  viewUserDocument.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Tooltip, Button } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import UserDocumentProxy from "../../utility/proxy/manager/userDocumentProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import { SimCardDownloadSharp } from "@mui/icons-material";
import { BsClipboard2CheckFill } from "react-icons/bs";
import { BsClipboard2XFill } from "react-icons/bs";
import ConfirmationModal from "../common/confirmationModal";
import UserProxy from "../../utility/proxy/manager/userProxy";

export default function ViewUserDocument({ userId, onKycUpdate, kyc }) {
    const [documentData, setDocumentData] = useState([]);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showViewDocLoadSpinner, setShowViewDocLoadSpinner] = useState(true);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isApprove, setIsApprove] = useState(false);
    const [confirmModalMessage, setConfirmModalMessage] = useState("");
    const [confirmModalTitle, setConfirmModalTitle] = useState("");
    const [KycLoader, setKycLoader] = useState(false);
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);

    useEffect(() => {
        fetchDocdata();
    }, []);

    const fetchDocdata = (bAnimateSpinner) => {
        bAnimateSpinner && setShowViewDocLoadSpinner(true);
        UserDocumentProxy.getDocumentByUserID(
            userId,
            (responseData) => {
                if (responseData) {
                    // SUCCESS
                    setDocumentData(responseData);
                    setShowViewDocLoadSpinner(false);
                }
                else {
                    //FAILURE
                    setDocumentData([]);
                    setErrorSnackbar(true);
                    setShowViewDocLoadSpinner(false);
                }
            }
        )
    }

    const handleDocDownload = (data) => {
        let a = document.createElement("a");
        a.href = `data:application/;base64,${data.doc}`
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    const handleKycApproveOrReject = (isApprove) => {
        setIsApprove(isApprove);
        setShowConfirmationModal(true);
        setConfirmModalMessage("Are you sure you want to " + (isApprove ? "approve" : "reject") + " the KYC for this user?");
        setConfirmModalTitle("KYC " + (isApprove ? "Approve" : "Reject"));
    }

    const setSnackbarState = (show, message, variant) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
    };

    const handleKyc = () => {
        setKycLoader(true);
        UserProxy.UpdateUserKyc(userId, isApprove, (response) => {
            if (response) {
                setKycLoader(false);
                setShowConfirmationModal(false);
                setSnackbarState(true, "KYC updated successfully", "success");
                fetchDocdata();
                onKycUpdate(isApprove);
            } else {
                setKycLoader(false);
                setShowConfirmationModal(false);
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
            }
        })
    }

    const columnsTable = [
        {
            field: "docid",
            headerName: "DOCUMENT ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "doctype",
            headerName: "DOCUMENT TYPE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "doc",
            headerName: "DOWNLOAD",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: (params) => (
                <Box
                    style={{ textAlign: "center", cursor: "pointer" }}
                    onClick={() => handleDocDownload(params.row)}>
                    <SimCardDownloadSharp size={25} color="success" />
                </Box>
            ),
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },

    ]

    const rows = documentData;
    const columns = columnsTable;

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" :
                    snackbarVariant === "error" ? "Failed" : "Warning"}
                handleClose={() => setShowSnackbar(false)}
            />
            {showConfirmationModal ? (
                <ConfirmationModal
                    show={showConfirmationModal}
                    setShow={setShowConfirmationModal}
                    handleSave={handleKyc}
                    modalTitle={confirmModalTitle}
                    modalMessage={confirmModalMessage}
                    loading={KycLoader}
                />
            ) : null}
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: "70vh",
                }}>
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },

                    }}
                    slots={{ toolbar: GridToolbar }}
                    rows={rows}
                    columns={columns}
                    density="compact"
                    getRowId={(row) => row.docid}
                    loading={showViewDocLoadSpinner}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns status and traderName, the other columns will remain visible
                                docid: false,
                                // locationid: false,
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                />
                < Box style={{
                    position: "absolute",
                    top: 5,
                    right: "270px",
                    display: "flex",
                    flexWrap: "nowrap",
                }}>
                    {
                        showViewDocLoadSpinner ? (
                            <CircularProgress size="1rem" sx={{ lineHeight: - 2 }} />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    color="primary"
                                    fontSize="medium"
                                    onClick={() => fetchDocdata(true)}
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}
                    {!(userId === Constants.loggedInUser.id) ? (
                        <>
                            {kyc ? (
                                <Tooltip title="KYC Reject">
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        color="info"
                                        sx={{ ml: "5px" }}
                                        startIcon={<BsClipboard2XFill />}
                                        onClick={() => handleKycApproveOrReject(false)}
                                    >
                                        KYC Reject
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Tooltip title="KYC Approve">
                                    <Button
                                        component="label"
                                        variant="contained"
                                        size="small"
                                        sx={{ ml: "5px" }}
                                        startIcon={<BsClipboard2CheckFill />}
                                        onClick={() => handleKycApproveOrReject(true)}
                                    >
                                        KYC Approve
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    ) : null}
                </Box>
            </Box>
        </Box>
    )
}